import { useEffect, useCallback, useState, createContext, useContext } from 'react';

export interface KeyboardShortcut {
  key: string;
  modifiers?: {
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    meta?: boolean;
  };
  description: string;
  category: string;
  action: () => void;
  enabled?: boolean;
}

interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  meta?: boolean;
}

function parseShortcut(shortcut: string): ShortcutConfig {
  const parts = shortcut.toLowerCase().split('+');
  const key = parts[parts.length - 1];
  return {
    key,
    ctrl: parts.includes('ctrl') || parts.includes('control'),
    alt: parts.includes('alt'),
    shift: parts.includes('shift'),
    meta: parts.includes('meta') || parts.includes('cmd') || parts.includes('command'),
  };
}

/**
 * True when the event target is somewhere the user is typing or selecting — a
 * form control, contentEditable region, or an open dropdown/listbox/menu. Global
 * shortcuts (single-key nav especially) must not fire in these contexts, or a
 * stray keystroke while filling a field or picking from a dropdown hijacks it.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;

  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;

  // Custom dropdowns/comboboxes/menus render as buttons or divs carrying an ARIA
  // role. Opt-out anywhere else with the `data-no-shortcuts` attribute.
  if (
    typeof el.closest === 'function' &&
    el.closest('[role="listbox"],[role="combobox"],[role="menu"],[data-no-shortcuts]')
  ) {
    return true;
  }

  return false;
}

function matchesShortcut(event: KeyboardEvent, config: ShortcutConfig): boolean {
  const key = event.key.toLowerCase();
  const keyMatch = key === config.key || event.code.toLowerCase() === `key${config.key}`;

  return (
    keyMatch &&
    !!event.ctrlKey === !!config.ctrl &&
    !!event.altKey === !!config.alt &&
    !!event.shiftKey === !!config.shift &&
    !!event.metaKey === !!config.meta
  );
}

export function useKeyboardShortcut(
  shortcut: string,
  callback: () => void,
  options: { enabled?: boolean; preventDefault?: boolean } = {}
) {
  const { enabled = true, preventDefault = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const config = parseShortcut(shortcut);

    const handler = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs or selecting in a dropdown
      if (isEditableTarget(event.target)) {
        return;
      }

      if (matchesShortcut(event, config)) {
        if (preventDefault) {
          event.preventDefault();
        }
        callback();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [shortcut, callback, enabled, preventDefault]);
}

// Global shortcut registry for showing help
interface ShortcutRegistry {
  shortcuts: KeyboardShortcut[];
  registerShortcut: (shortcut: KeyboardShortcut) => void;
  unregisterShortcut: (key: string) => void;
  isHelpOpen: boolean;
  setHelpOpen: (open: boolean) => void;
}

const ShortcutContext = createContext<ShortcutRegistry | null>(null);

export function useShortcutRegistry() {
  const context = useContext(ShortcutContext);
  if (!context) {
    throw new Error('useShortcutRegistry must be used within ShortcutProvider');
  }
  return context;
}

export function useShortcutRegistryState(): ShortcutRegistry {
  const [shortcuts, setShortcuts] = useState<KeyboardShortcut[]>([]);
  const [isHelpOpen, setHelpOpen] = useState(false);

  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    setShortcuts((prev) => {
      const filtered = prev.filter((s) => s.key !== shortcut.key);
      return [...filtered, shortcut];
    });
  }, []);

  const unregisterShortcut = useCallback((key: string) => {
    setShortcuts((prev) => prev.filter((s) => s.key !== key));
  }, []);

  return {
    shortcuts,
    registerShortcut,
    unregisterShortcut,
    isHelpOpen,
    setHelpOpen,
  };
}

export { ShortcutContext };

// Format shortcut for display
const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export function formatShortcutKey(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];
  const mods = shortcut.modifiers || {};

  if (mods.ctrl) parts.push(isMac ? '⌃' : 'Ctrl');
  if (mods.meta) parts.push(isMac ? '⌘' : 'Win');
  if (mods.alt) parts.push(isMac ? '⌥' : 'Alt');
  if (mods.shift) parts.push(isMac ? '⇧' : 'Shift');

  const keyDisplay = shortcut.key.length === 1
    ? shortcut.key.toUpperCase()
    : shortcut.key.charAt(0).toUpperCase() + shortcut.key.slice(1);
  parts.push(keyDisplay);

  return isMac ? parts.join('') : parts.join('+');
}

// Predefined shortcuts
export const COMMON_SHORTCUTS = {
  // Navigation
  goToHome: { key: 'shift+g', category: 'Navigation', description: 'Go to Home' },
  goToTickets: { key: 'shift+t', category: 'Navigation', description: 'Go to Tickets' },
  goToCustomers: { key: 'shift+c', category: 'Navigation', description: 'Go to Customers' },
  goToSettings: { key: 'shift+s', category: 'Navigation', description: 'Go to Settings' },

  // Global
  openSearch: { key: 'meta+k', category: 'Global', description: 'Open search' },
  showShortcuts: { key: 'shift+/', category: 'Global', description: 'Show keyboard shortcuts' },

  // Tickets
  newTicket: { key: 'n', category: 'Tickets', description: 'New ticket' },
  replyToTicket: { key: 'r', category: 'Tickets', description: 'Reply to ticket' },
  resolveTicket: { key: 'e', category: 'Tickets', description: 'Resolve ticket' },
  assignTicket: { key: 'a', category: 'Tickets', description: 'Assign ticket' },
  nextTicket: { key: 'j', category: 'Tickets', description: 'Next ticket' },
  prevTicket: { key: 'k', category: 'Tickets', description: 'Previous ticket' },

  // Editor
  sendMessage: { key: 'meta+enter', category: 'Editor', description: 'Send message' },
};

// Group shortcuts by category
export function groupShortcutsByCategory(
  shortcuts: KeyboardShortcut[]
): Map<string, KeyboardShortcut[]> {
  const groups = new Map<string, KeyboardShortcut[]>();
  const categoryOrder = ['Global', 'Navigation', 'Tickets', 'Editor', 'General'];

  // Initialize with empty arrays in order
  for (const cat of categoryOrder) {
    groups.set(cat, []);
  }

  for (const shortcut of shortcuts) {
    const category = shortcut.category || 'General';
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(shortcut);
  }

  // Remove empty categories
  for (const [key, value] of groups) {
    if (value.length === 0) {
      groups.delete(key);
    }
  }

  return groups;
}
